import { useCallback, useEffect, useState } from "react";
import type {
  FaqEntryDto,
  FaqUpsert,
  LocalizedText,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { createFaq, deleteFaq, listFaq, updateFaq } from "@/lib/faq-api";

// TES-214 — FAQ management. A flat list of Q&A entries with inline create/edit/
// delete + a published toggle. Localized (sv/en). Patients see the published
// entries in the app's Messages area.

const empty = (): LocalizedText => ({ sv: "", en: "" });

type Draft = {
  question: LocalizedText;
  answer: LocalizedText;
  category: string;
  order: number;
  published: boolean;
};

const toDraft = (e: FaqEntryDto): Draft => ({
  question: e.question,
  answer: e.answer,
  category: e.category ?? "",
  order: e.order,
  published: e.published,
});

const toUpsert = (d: Draft): FaqUpsert => ({
  question: d.question,
  answer: d.answer,
  category: d.category.trim() || undefined,
  order: d.order,
  published: d.published,
});

function Editor({
  draft,
  onChange,
}: {
  draft: Draft;
  onChange: (d: Draft) => void;
}) {
  const field =
    "w-full border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand";
  return (
    <div className="flex flex-col gap-sm">
      {(["sv", "en"] as const).map((loc) => (
        <div key={loc} className="grid grid-cols-2 gap-sm">
          <input
            className={field}
            placeholder={`Fråga (${loc.toUpperCase()})`}
            value={draft.question[loc]}
            onChange={(e) =>
              onChange({
                ...draft,
                question: { ...draft.question, [loc]: e.target.value },
              })
            }
          />
          <input
            className={field}
            placeholder={`Svar (${loc.toUpperCase()})`}
            value={draft.answer[loc]}
            onChange={(e) =>
              onChange({
                ...draft,
                answer: { ...draft.answer, [loc]: e.target.value },
              })
            }
          />
        </div>
      ))}
      <div className="flex items-center gap-md">
        <input
          className={`${field} w-40`}
          placeholder="Kategori (valfritt)"
          value={draft.category}
          onChange={(e) => onChange({ ...draft, category: e.target.value })}
        />
        <label className="flex items-center gap-xs text-sm">
          Ordning
          <input
            type="number"
            className={`${field} w-20`}
            value={draft.order}
            onChange={(e) =>
              onChange({ ...draft, order: Number(e.target.value) || 0 })
            }
          />
        </label>
        <label className="flex items-center gap-xs text-sm">
          <input
            type="checkbox"
            checked={draft.published}
            onChange={(e) =>
              onChange({ ...draft, published: e.target.checked })
            }
          />
          Publicerad
        </label>
      </div>
    </div>
  );
}

export function FaqListRoute() {
  const { signOut } = useAuth();
  const [rows, setRows] = useState<FaqEntryDto[] | null>(null);
  const [edits, setEdits] = useState<Record<string, Draft>>({});
  const [creating, setCreating] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) return void signOut();
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const reload = useCallback(() => {
    listFaq()
      .then((list) => {
        setRows(list);
        setEdits(Object.fromEntries(list.map((e) => [e.id, toDraft(e)])));
      })
      .catch(handleError);
  }, [handleError]);

  useEffect(reload, [reload]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      reload();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">
          Frågor och svar
        </h1>
        <button
          type="button"
          onClick={() =>
            setCreating({
              question: empty(),
              answer: empty(),
              category: "",
              order: rows?.length ?? 0,
              published: true,
            })
          }
          className="rounded-md bg-brand text-white px-md py-sm text-sm font-medium hover:bg-brand/90"
        >
          Ny fråga
        </button>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {creating ? (
        <div className="bg-surface border border-brand/40 rounded-md p-md flex flex-col gap-sm">
          <Editor draft={creating} onChange={setCreating} />
          <div className="flex gap-sm">
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await createFaq(toUpsert(creating));
                  setCreating(null);
                })
              }
              className="px-md py-sm rounded-md bg-brand text-white text-sm disabled:opacity-50"
            >
              Skapa
            </button>
            <button
              type="button"
              onClick={() => setCreating(null)}
              className="px-md py-sm rounded-md border border-surfaceMuted text-sm text-textSecondary"
            >
              Avbryt
            </button>
          </div>
        </div>
      ) : null}

      {rows?.map((e) => {
        const d = edits[e.id];
        if (!d) return null;
        const dirty = JSON.stringify(toDraft(e)) !== JSON.stringify(d);
        return (
          <div
            key={e.id}
            className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm"
          >
            <Editor
              draft={d}
              onChange={(next) => setEdits((s) => ({ ...s, [e.id]: next }))}
            />
            <div className="flex items-center gap-sm">
              <button
                type="button"
                disabled={busy || !dirty}
                onClick={() => run(() => updateFaq(e.id, toUpsert(d)))}
                className="px-md py-sm rounded-md bg-brand text-white text-sm disabled:opacity-50"
              >
                Spara
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => deleteFaq(e.id))}
                className="ml-auto px-md py-sm rounded-md border border-surfaceMuted text-sm text-textSecondary hover:text-danger hover:border-danger disabled:opacity-50"
              >
                Ta bort
              </button>
            </div>
          </div>
        );
      })}

      {rows && rows.length === 0 && !creating ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          Inga frågor ännu. Lägg till med “Ny fråga”.
        </div>
      ) : null}
    </div>
  );
}
